import React, { useEffect, FC, useState, SyntheticEvent } from "react";
import { withRouter } from "react-router-dom";
import { generalCRUD } from "../../../actions/generalCRUD";
import helpFunctions from "../../../tools/helpFunctions";
import { useSelector } from "react-redux";

// Components
import MainTemplate from "../templates/MainTemplate";
import TopPartPage from "../../molecules/TopPartPage";
import ModalWindow from "../../molecules/ModalWindow";
import LoaderLocal from "../../atoms/LoaderLocal";
import { toastr } from "react-redux-toastr";
import CustomPagination from "../../organisms/CustomPagination";
import TextField from "@material-ui/core/TextField";

// Styles
import "./style.scss";

import { rootReducerType } from "../../../redux/reducers";
import { FieldType } from "../../../validator/types";
import { maxLengthInstance } from "../../../validator/Fields/instantLCLQuote";
import { validateAllFields, validateField } from "../../../validator/Fields";

export type citiesItemType = {
  id: number;
  code: string;
  name: string;
  [key: string]: string | number;
};

export const citiesItemInitial = {
  id: 1,
  code: "",
  name: "",
};

type editModalType = {
  status: boolean;
  index: number;
};

const CitiesContainer: FC = () => {
  const quantityPerPage = useSelector((state: rootReducerType) => state.app.quantityPerPage);
  const [citiesList, setCitiesList] = useState<Array<citiesItemType>>([]);
  const [citiesItem, setCitiesItem] = useState<citiesItemType>(citiesItemInitial);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<editModalType>({
    status: false,
    index: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paginationSettings, setPaginationSettings] = useState({
    page: 1,
    maxPage: 1,
  });

  /*Validation*/
  const [nameValid, setNameValid] = useState<FieldType>({ ...maxLengthInstance(150) });
  const [codeValid, setCodeValid] = useState<FieldType>({ ...maxLengthInstance(50) });

  // to get all entities
  useEffect(() => {
    getAllCities(1);
  }, []);

  const openEditModal = (data: citiesItemType, index: number): void => {
    setEditModal({
      status: true,
      index: index,
    });
    setCitiesItem(data);
  };

  const allFields = [
    { validate: { ...nameValid, value: citiesItem.name }, setValidate: setNameValid },
    { validate: { ...codeValid, value: citiesItem.code }, setValidate: setCodeValid },
  ];

  const closeModal = (): void => {
    setCitiesItem(citiesItemInitial);
    isAddModalOpen
      ? setIsAddModalOpen(false)
      : setEditModal({
          ...editModal,
          status: false,
        });
    allFields.forEach((field) => {
      if (!field.validate.valid) {
        field.setValidate((prevState) => ({ ...prevState, valid: null, error: "", value: "" }));
      }
    });
  };

  const getAllCities = async (page: number, quantity = quantityPerPage) => {
    setIsLoading(true);
    const response = await generalCRUD.getAllData("city", page, undefined, quantity);
    if (response.code === 200) {
      setCitiesList(response.data.items);
      const maxPage = Math.ceil(response.data.totalCount / response.data.numItemsPerPage);
      if (maxPage < page) return getAllCities(maxPage, quantity);
      setPaginationSettings({
        page: response.data.currentPageNumber,
        maxPage: maxPage,
      });
    }
    setIsLoading(false);
  };

  const sendRequest = async (handleSuccess: () => void, forEdit?: boolean) => {
    let valid = true;
    for (const key in citiesItem) {
      if (key === "id") continue;
      if (!citiesItem[key]) {
        valid = false;
        break;
      }
    }
    if (valid) {
      setIsLoading(true);
      closeModal();
      let response;
      if (forEdit) {
        response = await generalCRUD.editConcreteData("city", citiesItem.id, citiesItem);
      } else {
        response = await generalCRUD.addNewData("city", citiesItem);
      }
      if (response.code === 200) {
        handleSuccess();
        return setIsLoading(false);
      }
      setIsLoading(false);
      return toastr.error(
        "Error",
        response.message === "Entity error." ? "City with this code ID already exists" : response.error,
      );
    } else {
      toastr.error("Error", "Not all fields are filled in");
    }
  };

  const addEntity = async () => {
    const handleSuccess = () => {
      getAllCities(paginationSettings.page);
      toastr.success("New City was added successfully", "");
    };
    if (validateAllFields(allFields)) {
      sendRequest(handleSuccess);
    }
  };

  const editEntity = (index: number): void => {
    const handleSuccess = () => {
      const newData: Array<citiesItemType> = [...citiesList];
      newData[index] = { ...citiesItem };
      setCitiesList(newData);
      toastr.success("City was edited successfully", "");
    };
    if (validateAllFields(allFields)) {
      sendRequest(handleSuccess, true);
    }
  };

  const textChangeHandler = (event: SyntheticEvent, field: string, num?: boolean | string) => {
    const value = (event.target as HTMLInputElement).value;
    if (num && value.length && !helpFunctions.isNumber(value, num)) return false;
    const newData = {
      ...citiesItem,
      [field]: value,
    };
    setCitiesItem(newData);
  };
  const modalContent = (
    <>
      <div className="unit-wrap">
        <div className="unit">
          <p className="sub-title">City name</p>
          <p>
            <TextField
              helperText={nameValid.error}
              error={nameValid.valid === false}
              value={citiesItem.name}
              InputProps={{ disableUnderline: true, inputProps: { maxLength: nameValid.maxLength } }}
              type="text"
              placeholder="Name"
              onChange={(event) => textChangeHandler(event, "name")}
              onBlur={(e) => validateField(e.target.value, nameValid, setNameValid)}
            />
          </p>
        </div>
      </div>
      <div className="unit-wrap">
        <div className="unit">
          <p className="sub-title">Code ID</p>
          <p>
            <TextField
              helperText={codeValid.error}
              error={codeValid.valid === false}
              value={citiesItem.code}
              InputProps={{ disableUnderline: true, inputProps: { maxLength: codeValid.maxLength } }}
              type="text"
              placeholder="Code ID"
              onChange={(event) => textChangeHandler(event, "code")}
              onBlur={(e) => validateField(e.target.value, codeValid, setCodeValid)}
            />
          </p>
        </div>
      </div>
    </>
  );
  return (
    <MainTemplate>
      <div id="cities" className="page-container">
        <TopPartPage title="cities" buttonTitle="add new city" handleClick={() => setIsAddModalOpen(true)} />
        <div className="table-thead">
          <div className="th first">ID</div>
          <div className="middle-part">
            <div className="th">Code ID</div>
            <div className="th">Name</div>
          </div>
          <div className="th last">Action</div>
        </div>
        <ul className="table-content">
          {citiesList.map((item: citiesItemType, index) => {
            return (
              <li className="table-content-item" key={index}>
                <div className="th first">{item.id}</div>
                <div className="middle-part">
                  <div className="th">{item.code}</div>
                  <div className="th">{item.name}</div>
                </div>
                <div className="th last">
                  <span onClick={() => openEditModal(item, index)}>Edit</span>
                </div>
              </li>
            );
          })}
        </ul>
        <CustomPagination
          page={paginationSettings.page}
          maxPage={paginationSettings.maxPage}
          isLoaded={!isLoading}
          itemsNumber={citiesList.length}
          handleClickPage={(value) => getAllCities(value)}
          handleClickQuantity={(quantity) => getAllCities(paginationSettings.page, quantity)}
        />
        {isLoading && <LoaderLocal />}
        <ModalWindow
          open={isAddModalOpen}
          handleClose={closeModal}
          handleSubmit={addEntity}
          title="Add new city"
          classes="cities-modal"
          description="You can add new City"
        >
          {modalContent}
        </ModalWindow>
        <ModalWindow
          open={editModal.status}
          handleClose={closeModal}
          handleSubmit={() => editEntity(editModal.index)}
          title="Edit city"
          classes="cities-modal"
          description="You can edit City"
        >
          {modalContent}
        </ModalWindow>
      </div>
    </MainTemplate>
  );
};

export default withRouter(CitiesContainer);
